export type ParsedSessionId =
  | { sessionId: string }
  | { error: string };

/**
 * Accept a raw Firestore session ID or a Guest Session share URL
 * (`/app/{schoolId}?sessionId={id}`) and return the ID to look up.
 */
export function parseSessionIdInput(raw: string): ParsedSessionId {
  const input = stripWrappingQuotes(raw.trim());
  if (!input) {
    return { error: 'Enter a session ID or a share link' };
  }

  const fromQuery = extractSessionIdParam(input);
  if (fromQuery) {
    return { sessionId: fromQuery };
  }

  if (looksLikeShareLink(input)) {
    return { error: 'Could not find a session ID in that URL. Paste the full share link or the session ID.' };
  }

  return { sessionId: input };
}

function stripWrappingQuotes(value: string): string {
  return value.replace(/^['"]+|['"]+$/g, '');
}

function extractSessionIdParam(input: string): string | null {
  for (const candidate of urlCandidates(input)) {
    try {
      const sessionId = new URL(candidate).searchParams.get('sessionId')?.trim();
      if (sessionId) {
        return sessionId;
      }
    } catch {
      // Try the next candidate or the regex fallback below.
    }
  }

  const match = input.match(/[?&]sessionId=([^&#\s]+)/i);
  if (!match?.[1]) {
    return null;
  }
  try {
    return decodeURIComponent(match[1].trim());
  } catch {
    return match[1].trim();
  }
}

function urlCandidates(input: string): string[] {
  if (/^https?:\/\//i.test(input)) {
    return [input];
  }
  if (input.startsWith('//')) {
    return [`https:${input}`];
  }
  if (input.startsWith('?')) {
    return [`https://placeholder.local/${input}`];
  }
  if (input.includes('?') || input.includes('/')) {
    const withoutLeadingSlash = input.replace(/^\/+/, '');
    return [
      `https://${withoutLeadingSlash}`,
      `https://placeholder.local/${withoutLeadingSlash}`,
    ];
  }
  return [];
}

function looksLikeShareLink(input: string): boolean {
  return /^(https?:)?\/\//i.test(input)
    || /[?&]sessionId=/i.test(input)
    || /\/app\//i.test(input)
    || input.includes('?');
}
